/**
 * Media playback execution blocks — work on any Playable behavior (Video,
 * AudioClip, Animation, Pages). Following KHR_interactivity's `animation/*`
 * convention, actions are split into separate single-purpose blocks rather
 * than gated on an action enum:
 *
 *   trace/playMedia        — start/resume playback
 *   trace/pauseMedia       — pause at current position
 *   trace/stopMedia        — stop and reset to 0
 *   trace/seekMedia        — jump to a specific time
 *   trace/setMediaVolume   — set volume (only honored on volume-capable targets)
 *   trace/isMediaPlaying   — data: whether playback is running (pull-evaluated)
 *   trace/getMediaVolume   — data: current volume 0-1 (pull-evaluated)
 *
 * `playMedia` additionally accepts an optional `src` data input — when the
 * target implements PlayableSource (Video, AudioClip), the URL is swapped
 * before playback starts. Other Playables (Animation, Pages) ignore `src`.
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeNumber, RichTypeString, RichTypeBoolean } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { hasPlayableSource, hasPlayableVolume, hasPlayableState, hasPlayableVolumeReader } from '../../playable';
import { Logger } from '../../../utils/Logger';
import { resolvePlayable } from './mediaTarget';

// ── trace/playMedia ──────────────────────────────────────────────────────

export class TracePlayMediaBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        // Empty string sentinel = "no override" — keeps the input typed as string
        // (no `string | undefined` gymnastics in graph data).
        this.registerDataInput("src", RichTypeString, "");
        this.registerDataInput("volume", RichTypeNumber, -1);
        this.registerDataInput("seek", RichTypeNumber, -1);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const resolved = resolvePlayable(context, this._targetPath);
        if (!resolved) {
            Logger.warn(`[TracePlayMediaBlock] No Playable behavior on target "${this._targetPath}"`);
            this.out._activateSignal(context);
            return;
        }
        const { playable } = resolved;

        const src = this.getDataInput("src")!.getValue(context) as string;
        const volume = this.getDataInput("volume")!.getValue(context) as number;
        const seek = this.getDataInput("seek")!.getValue(context) as number;

        const applyVolumeSeekPlay = (): void => {
            if (volume >= 0 && hasPlayableVolume(playable)) {
                playable.setVolume(volume);
            }
            if (seek >= 0) {
                playable.seek(seek);
            }
            void playable.play();
        };

        if (src && hasPlayableSource(playable)) {
            // setSource resolves on media-ready. seek/volume/play must wait —
            // calling them sooner would land on the *old* element (or no
            // element, if the texture is in the released-not-loaded gap).
            // The out signal fires immediately so downstream flow proceeds in
            // parallel with the load; per-target chaining stays correct
            // because the seek/volume/play closure captures the playable ref.
            const result = playable.setSource(src);
            if (result && typeof result.then === 'function') {
                // Log and continue if the load fails — the out signal has
                // already fired downstream, so swallowing here is the only
                // option, but a silent failure leaves a black/silent target
                // with no diagnostic. Don't fall through to applyVolumeSeekPlay
                // on rejection: the underlying element wasn't replaced.
                void result.then(applyVolumeSeekPlay).catch((err: unknown) => {
                    const msg = err instanceof Error ? err.message : String(err);
                    Logger.warn(`[TracePlayMediaBlock] setSource failed for "${this._targetPath}": ${msg}`);
                });
            } else {
                applyVolumeSeekPlay();
            }
        } else {
            applyVolumeSeekPlay();
        }

        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TracePlayMediaBlock";
    }
}

// ── trace/pauseMedia ─────────────────────────────────────────────────────

export class TracePauseMediaBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const resolved = resolvePlayable(context, this._targetPath);
        if (!resolved) {
            this.out._activateSignal(context);
            return;
        }
        resolved.playable.pause();
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TracePauseMediaBlock";
    }
}

// ── trace/stopMedia ──────────────────────────────────────────────────────

export class TraceStopMediaBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const resolved = resolvePlayable(context, this._targetPath);
        if (!resolved) {
            this.out._activateSignal(context);
            return;
        }
        resolved.playable.stop();
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceStopMediaBlock";
    }
}

// ── trace/seekMedia ──────────────────────────────────────────────────────

export class TraceSeekMediaBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("time", RichTypeNumber, 0);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const resolved = resolvePlayable(context, this._targetPath);
        if (!resolved) {
            this.out._activateSignal(context);
            return;
        }
        const time = this.getDataInput("time")!.getValue(context) as number;
        resolved.playable.seek(time);
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSeekMediaBlock";
    }
}

// ── trace/setMediaVolume ─────────────────────────────────────────────────

export class TraceSetMediaVolumeBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("volume", RichTypeNumber, 1);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const resolved = resolvePlayable(context, this._targetPath);
        if (!resolved) {
            this.out._activateSignal(context);
            return;
        }
        if (hasPlayableVolume(resolved.playable)) {
            const volume = this.getDataInput("volume")!.getValue(context) as number;
            resolved.playable.setVolume(volume);
        }
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSetMediaVolumeBlock";
    }
}

// ── trace/isMediaPlaying — data block ────────────────────────────────────

export class TraceIsMediaPlayingBlock extends FlowGraphBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("value", RichTypeBoolean);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const resolved = resolvePlayable(context, this._targetPath);
        const playable = resolved?.playable;
        // Unresolvable targets and Playables without state (Pages) read as
        // not-playing rather than erroring — same tolerance as the media
        // execution blocks.
        const playing = !!playable && hasPlayableState(playable) && playable.isPlaying();
        this.getDataOutput("value")!.setValue(playing, context);
    }

    public override getClassName(): string {
        return "TraceIsMediaPlayingBlock";
    }
}

// ── trace/getMediaVolume — data block ────────────────────────────────────

export class TraceGetMediaVolumeBlock extends FlowGraphBlock {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataOutput("value", RichTypeNumber);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const resolved = resolvePlayable(context, this._targetPath);
        const playable = resolved?.playable;
        // 1 (the HTMLMediaElement default) for unresolvable targets and
        // Playables without a volume, so "no volume control" reads as unmuted.
        const volume = playable && hasPlayableVolumeReader(playable) ? playable.getVolume() : 1;
        this.getDataOutput("value")!.setValue(volume, context);
    }

    public override getClassName(): string {
        return "TraceGetMediaVolumeBlock";
    }
}
